import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { REMOTE_FEATURE_LABELS } from '@ids-constants';
import { TargetDeviceService } from '@ids-services';
import { FormBuilderComponent } from '@microsec/components';
import { SAVE_CHANGES_LABEL } from '@microsec/constants';
import { FormItem } from '@microsec/models';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';

const FORM_PARAMS = {
  INTERFACES: 'pcap_capture_interfaces',
  CAPTURE_LEVEL_FILTERS: 'pcap_capture_filters',
  DURATION: 'pcap_capture_capture_duration',
  MAX_FILE_SIZE: 'pcap_capture_max_file_size',
  MAX_FILES: 'pcap_capture_max_files',
  OVERFLOW_STRATEGY: 'pcap_capture_overflow_strategy',
};

@Component({
  selector: 'app-microagent-config-pcap-form',
  templateUrl: './microagent-config-pcap-form.component.html',
  styleUrls: ['./microagent-config-pcap-form.component.scss'],
  imports: [FormBuilderComponent],
})
export class MicroagentConfigPcapFormComponent extends BaseComponent implements AfterViewInit {
  deviceId: any = null;

  configuration: any = null;

  feature: any = null;

  interfaceOptions: any[] = [];

  fields: FormItem[] = [];

  @ViewChild('fb') form!: FormBuilderComponent;

  FORM_PARAMS = FORM_PARAMS;

  SAVE_CHANGES_LABEL = SAVE_CHANGES_LABEL;

  constructor(
    private targetDeviceService: TargetDeviceService,
    public dialogConfig: DynamicDialogConfig,
  ) {
    super();
  }

  async ngAfterViewInit() {
    await this.prepareConfigs();
    this.deviceId = this.dialogConfig?.data?.deviceId;
    this.configuration = this.dialogConfig?.data?.configuration;
    this.feature = this.dialogConfig?.data?.feature;
    this.interfaceOptions = this.dialogConfig?.data?.interfaceOptions;
    this.initForm();
  }

  initForm() {
    const fields: FormItem[] = [
      Object.assign(new FormItem(), {
        label: 'PCAP Capture Settings',
        labelStyleClass: 'font-bold',
        field: 'text',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.INTERFACES,
        label: 'Interface(s) to Capture',
        required: true,
        options: this.interfaceOptions,
        field: 'multiselect',
        fieldInfo: 'Only the selected interface(s) will be captured',
        placeholder: 'Select interface on this device',
        defaultValue: [],
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.CAPTURE_LEVEL_FILTERS,
        label: 'Capture Level Filters',
        required: false,
        field: 'input-chip',
        fieldInfo: 'To include multiple filters, use commas to separate them e.g. tcp',
        placeholder: 'tcp, udp',
        defaultValue: [],
      } as FormItem),
      Object.assign(new FormItem(), {
        hasNoLabel: true,
        rowStyleClass: 'text-left -mt-5',
        field: 'link',
        url: 'https://www.tcpdump.org/manpages/pcap-filter.7.html',
        defaultValue: 'Filter Syntax Guide',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.DURATION,
        label: 'Capture Duration (s)',
        required: true,
        field: 'number',
        fieldInfo: 'How long to capture packets for',
        minValue: 0,
        defaultValue: 10,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.MAX_FILE_SIZE,
        label: 'Max file Size (bytes)',
        required: true,
        field: 'number',
        fieldInfo: 'Maximum size of each file',
        minValue: 0,
        maxValue: 1000000,
        defaultValue: 1000000,
      } as FormItem),
      Object.assign(new FormItem(), {
        field: 'divider',
      } as FormItem),
      Object.assign(new FormItem(), {
        label: 'Upload Failure Backup Settings',
        labelStyleClass: 'font-bold',
        field: 'text',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.MAX_FILES,
        label: 'Max Files on Device',
        required: true,
        field: 'number',
        fieldInfo: 'If the file fails to upload, how many files should be stored on the device',
        minValue: 0,
        defaultValue: 10,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.OVERFLOW_STRATEGY,
        label: 'Overflow Strategy',
        required: true,
        field: 'radio',
        layout: 'horizontal',
        options: [
          { label: 'Delete Oldest File', value: 'delete_oldest' },
          { label: 'Pause Capture', value: 'pause_capture' },
        ],
        fieldInfo: 'What happens when the number of files to store goes beyond the set maximum',
        minValue: 0,
        defaultValue: 'delete_oldest',
      } as FormItem),
    ];
    this.fields = fields;
    this.form?.patchValue({
      ...this.configuration?.[this.feature],
    });
  }

  onSubmit(closeDialog: () => void) {
    this.form.isLoading = true;
    const formData = this.form.getRawValue();

    const payload = {
      configuration: {
        [this.feature]: {
          ...formData,
        },
      },
    };

    this.targetDeviceService
      .updateDeviceConfiguration(this.deviceId, payload)
      .pipe(
        finalize(() => {
          this.form.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage(`${REMOTE_FEATURE_LABELS[this.feature] || this.feature} Configuration Updated Successfully`);
          closeDialog();
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }
}
